import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useWatch,
} from 'react-hook-form';

import { PropertySelect } from '../../../components/form/property-select.js';
import { type TaxFormInput } from '../api/schemas.js';

const TAX_TYPE_OPTIONS = [
  { value: 'GST', label: 'GST' },
  { value: 'VAT', label: 'VAT' },
  { value: 'CITY_TAX', label: 'City tax' },
  { value: 'TOURISM_TAX', label: 'Tourism tax' },
  { value: 'SERVICE_CHARGE', label: 'Service charge' },
];

const CHARGE_BASIS_OPTIONS = [
  { value: 'PERCENTAGE', label: 'Percentage' },
  { value: 'PER_STAY', label: 'Per stay' },
  { value: 'PER_NIGHT', label: 'Per night' },
  { value: 'PER_PERSON_PER_NIGHT', label: 'Per person per night' },
];

export function TaxFields({
  register,
  errors,
  control,
  isEdit,
}: {
  readonly register: UseFormRegister<TaxFormInput>;
  readonly errors: FieldErrors<TaxFormInput>;
  readonly control: Control<TaxFormInput>;
  readonly isEdit: boolean;
}): React.JSX.Element {
  const basis = useWatch({ control, name: 'basis' });

  return (
    <div className="space-y-4">
      <Controller
        control={control}
        name="propertyId"
        render={({ field, fieldState }) => (
          <PropertySelect
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message ?? errors.propertyId?.message}
            required
            readOnly={isEdit}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.name?.message} required>
          <FieldLabel>Name</FieldLabel>
          <FieldInput autoComplete="off" placeholder="GST 10%" {...register('name')} />
        </Field>

        <Controller
          control={control}
          name="type"
          render={({ field, fieldState }) => (
            <Field error={fieldState.error?.message ?? errors.type?.message} required>
              <FieldLabel>Tax type</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={TAX_TYPE_OPTIONS}
                placeholder="Choose a tax type"
                searchable
              />
            </Field>
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Controller
          control={control}
          name="basis"
          render={({ field, fieldState }) => (
            <Field error={fieldState.error?.message ?? errors.basis?.message} required>
              <FieldLabel>Charge basis</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={CHARGE_BASIS_OPTIONS}
                placeholder="Choose how the tax is calculated"
                searchable
              />
            </Field>
          )}
        />

        <Field error={errors.priority?.message} required>
          <FieldLabel>Priority</FieldLabel>
          <FieldInput type="number" autoComplete="off" {...register('priority')} />
        </Field>
      </div>

      {basis === 'PERCENTAGE' ? (
        <Field error={errors.rate?.message} required>
          <FieldLabel>Rate (%)</FieldLabel>
          <FieldInput autoComplete="off" placeholder="10.5" {...register('rate')} />
        </Field>
      ) : (
        <Field error={errors.amount?.message} required>
          <FieldLabel>Amount</FieldLabel>
          <FieldInput autoComplete="off" placeholder="12.50" {...register('amount')} />
        </Field>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.inclusive?.message}>
          <Controller
            control={control}
            name="inclusive"
            render={({ field }) => (
              <FieldCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                disabled={field.disabled ?? false}
              >
                Inclusive
              </FieldCheckbox>
            )}
          />
        </Field>
        <Field error={errors.compounds?.message}>
          <Controller
            control={control}
            name="compounds"
            render={({ field }) => (
              <FieldCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                disabled={field.disabled ?? false}
              >
                Compounds
              </FieldCheckbox>
            )}
          />
        </Field>
        <Field error={errors.appliesToFees?.message}>
          <Controller
            control={control}
            name="appliesToFees"
            render={({ field }) => (
              <FieldCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                disabled={field.disabled ?? false}
              >
                Applies to fees
              </FieldCheckbox>
            )}
          />
        </Field>
        <Field error={errors.enabled?.message}>
          <Controller
            control={control}
            name="enabled"
            render={({ field }) => (
              <FieldCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                disabled={field.disabled ?? false}
              >
                Enabled
              </FieldCheckbox>
            )}
          />
        </Field>
      </div>
    </div>
  );
}
